import { useMutation } from '@tanstack/react-query';
import { omit } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { toastApiError } from '@/queries/common';

import type { InspectionType } from '@/common/inspection';
import { DeleteConfirmDialog } from '@/components/soft-delete/DeleteConfirmDialog';
import { useSoftDeleteItem } from '@/hooks/use-soft-delete';
import { addInspection } from '@/queries/inspections/add-inspection';
import { updateInspection } from '@/queries/inspections/update-inspection';
import { queryClient } from '@/queries/query.client';
import { softDeleteTargets } from '@/queries/soft-delete/soft-delete';
import { ROLE } from '../../../common/role';
import { usePermission } from '../../../hooks/use-permission';
import { InspectionForm } from './inspection-form';

export type InspectionOverviewProps = {
	inspection: InspectionType | null;
	isNew?: boolean;
	assetId: string;
	title: string;
	/** Restore of a deleted item previewed read-only (GH #331), where Delete sits on a live one */
	restoreButton?: React.ReactNode;
};

export function InspectionOverview({ inspection, isNew, assetId, title, restoreButton }: InspectionOverviewProps) {
	const { t } = useTranslation();
	const shouldUpdateInspections = usePermission(ROLE.INSPECTIONS_UPDATE);
	const shouldCreateInspections = usePermission(ROLE.INSPECTIONS_CREATE);
	const shouldDeleteInspections = usePermission(ROLE.INSPECTIONS_DELETE);
	const navigate = useNavigate();
	const addNewInspectionApi = useMutation({
		mutationFn: async (data: Partial<InspectionType>) => {
			// API call to add new inspection
			console.log('Adding new inspection with data:', data);
			const mappedData = {
				...data,
				title,
			};
			return addInspection(assetId, mappedData);
		},
		onError(error) {
			toastApiError(error);
		},
		async onSuccess(data) {
			console.log('Add inspection successful:', data);
			toast.success(t('inspections.toast.addSuccess'));
			await queryClient.invalidateQueries({
				queryKey: ['assets', assetId, 'inspections'],
			});
			navigate(`/assets/${assetId}?tab=inspections`);
		},
	});

	const updateInspectionApi = useMutation({
		mutationFn: async ({ id, data }: { id: string; data: Partial<InspectionType> }) => {
			if (!assetId) {
				throw new Error(t('inspections.toast.assetIdRequiredForUpdate'));
			}
			if (!id) {
				throw new Error(t('inspections.toast.inspectionIdRequiredForUpdate'));
			}
			// API call to update existing inspection
			console.log('Updating inspection with data:', inspection?.id, data);
			const mappedData = {
				...data,
				title,
			};
			return updateInspection(assetId, id, omit(mappedData, ['id']));
		},
		onError(error) {
			toastApiError(error);
		},
		async onSuccess(data) {
			console.log('Update inspection successful:', data);
			toast.success(t('inspections.toast.updateSuccess'));
			await queryClient.invalidateQueries({
				queryKey: ['assets', assetId, 'inspections'],
			});
		},
	});

	// Delete with cascade (GH #331): the dialog shows what goes with the inspection; the toast offers Undo
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const deleteInspectionApi = useSoftDeleteItem({
		onDeleted: () => {
			setIsDeleteOpen(false);
			navigate(`/assets/${assetId}?tab=inspections`);
		},
	});

	if (!inspection && !isNew) {
		return <div>{t('inspections.detail.noData')}</div>;
	}
	const onSubmit = (data: Partial<InspectionType>) => {
		if (isNew && !shouldCreateInspections) {
			toast.error(t('inspections.toast.createPermissionDenied'));
			return;
		}
		if (!isNew && !shouldUpdateInspections) {
			toast.error(t('inspections.toast.updatePermissionDenied'));
			return;
		}
		// Update existing inspection
		if (!isNew && inspection?.id) {
			updateInspectionApi.mutate({
				id: inspection.id,
				data,
			});
			return;
		}
		// Add new inspection
		if (isNew) {
			addNewInspectionApi.mutate(data);
			return;
		}
		// This should not happen
		console.error('Invalid state: onSubmit called without valid inspection or isNew flag.');
	};

	const handleDelete = () => {
		// The delete button is already hidden without the role; this keeps the
		// handler from relying on that, the same way onSubmit does.
		if (!shouldDeleteInspections) {
			toast.error(t('inspections.toast.deletePermissionDenied'));
			return;
		}
		if (inspection?.id) {
			setIsDeleteOpen(true);
		}
	};
	const inspectionName = inspection?.title || t('common.entities.inspection', { count: 1 });

	return (
		<>
			<InspectionForm
				inspection={inspection}
				onSubmit={onSubmit}
				onDelete={handleDelete}
				isSubmitting={addNewInspectionApi.isPending || updateInspectionApi.isPending}
				assetId={assetId}
				restoreButton={restoreButton}
			/>
			{inspection?.id && (
				<DeleteConfirmDialog
					open={isDeleteOpen}
					onOpenChange={setIsDeleteOpen}
					target={softDeleteTargets.inspection(assetId, inspection.id)}
					name={inspectionName}
					isDeleting={deleteInspectionApi.isPending}
					onConfirm={({ acceptWorkPackageImpact }) =>
						deleteInspectionApi.mutate({
							target: softDeleteTargets.inspection(assetId, inspection.id as string),
							name: inspectionName,
							acceptWorkPackageImpact,
							successMessage: t('inspections.toast.deleteSuccess'),
						})
					}
				/>
			)}
		</>
	);
}
